/**
 * UtilizationInsightCard — rule/statistics-based "AI Insight" banner shown
 * above the Jira Utilization chart. Fetches a precomputed narrative + 4 key
 * metrics (average utilization, non-compliance issues, avg story
 * points/hour, total bugs assigned) from ml-analytics/utilization-insight/.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
	Box,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import { useSelector } from "react-redux";
import { getUtilizationInsight } from "../../../Service/Apis";

type MetricPayload = {
	value: number | null;
	change_pct: number | null;
};

type UtilizationInsightResponse = {
	period_weeks: number;
	metrics: {
		average_utilization: MetricPayload;
		non_compliance_issues: MetricPayload;
		avg_story_points_per_hour: MetricPayload;
		total_bugs_assigned: MetricPayload;
	};
	insight_text: string;
	risk_flag: boolean;
	insight_types: string[];
};

const INSIGHT_TYPE_LABELS: Record<string, string> = {
	performance_forecast: "Performance Forecast",
	risk_alert: "Risk Alerts",
	actionable_recommendation: "Actionable Recommendations",
};

/** Renders "vs previous N week(s)" using the same period_weeks the backend
 * actually used to compute both the current and prior comparison windows,
 * instead of a vague "vs last period". */
const formatPeriodLabel = (periodWeeks: number): string =>
	periodWeeks === 1 ? "vs previous week" : `vs previous ${periodWeeks} weeks`;

const ChangeIndicator = ({
	changePct,
	periodWeeks,
	invertColor = false,
}: {
	changePct: number | null;
	periodWeeks: number;
	/** For metrics where a decrease is the desirable direction (e.g. bugs, non-compliance). */
	invertColor?: boolean;
}) => {
	if (changePct === null || changePct === undefined) {
		return (
			<Typography variant="caption" color="text.secondary">
				No data for the previous {periodWeeks === 1 ? "week" : `${periodWeeks} weeks`}
			</Typography>
		);
	}
	const isFlat = Math.abs(changePct) < 0.01;
	const isPositive = changePct > 0;
	const good = isFlat ? null : invertColor ? !isPositive : isPositive;
	const color = isFlat ? "text.secondary" : good ? "success.main" : "error.main";
	const Icon = isFlat ? RemoveIcon : isPositive ? ArrowUpwardIcon : ArrowDownwardIcon;

	return (
		<Stack direction="row" spacing={0.5} alignItems="center">
			<Icon sx={{ fontSize: 14, color }} />
			<Typography variant="caption" sx={{ color, fontWeight: 600 }}>
				{Math.abs(changePct).toFixed(2)}% {formatPeriodLabel(periodWeeks)}
			</Typography>
		</Stack>
	);
};

const MetricTile = ({
	label,
	value,
	suffix,
	changePct,
	periodWeeks,
	invertColor,
}: {
	label: string;
	value: string;
	suffix?: string;
	changePct: number | null;
	periodWeeks: number;
	invertColor?: boolean;
}) => (
	<Box>
		<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
			{label}
		</Typography>
		<Typography variant="h5" sx={{ fontWeight: 700 }}>
			{value}
			{suffix ? (
				<Typography component="span" variant="body2" color="text.secondary">
					{" "}
					{suffix}
				</Typography>
			) : null}
		</Typography>
		<ChangeIndicator changePct={changePct} periodWeeks={periodWeeks} invertColor={invertColor} />
	</Box>
);

const UtilizationInsightCard = () => {
	const [insight, setInsight] = useState<UtilizationInsightResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const nrole = useSelector((state: any) => state.auth.selectedRole);

	useEffect(() => {
		let cancelled = false;

		const fetchInsight = async () => {
			setLoading(true);
			setError(null);
			try {
				const token = localStorage.getItem("token");
				const response = await axios.get(getUtilizationInsight, {
					params: { role: nrole ?? "Employee" },
					headers: { Authorization: `Bearer ${token}` },
				});
				if (!cancelled) {
					setInsight(response.data);
				}
			} catch (err) {
				if (!cancelled) {
					setError("Failed to load utilization insight.");
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchInsight();

		return () => {
			cancelled = true;
		};
	}, [nrole]);

	if (loading) {
		return (
			<Paper sx={{ m: 2, p: 4, display: "flex", justifyContent: "center" }}>
				<CircularProgress size={24} />
			</Paper>
		);
	}

	if (error || !insight) {
		return null;
	}

	const { metrics } = insight;
	const activeTypes = new Set(insight.insight_types);

	return (
		<Paper sx={{ m: 2, p: 4 }}>
			<Stack
				direction="row"
				spacing={2}
				sx={{
					bgcolor: "primary.50",
					backgroundColor: (theme) =>
						theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.12)" : "#eef4ff",
					borderRadius: 2,
					p: 2,
					mb: 3,
					alignItems: "flex-start",
					justifyContent: "space-between",
				}}
			>
				<Stack direction="row" spacing={1.5} alignItems="flex-start">
					<LightbulbOutlinedIcon color="primary" sx={{ mt: 0.3 }} />
					<Box>
						<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
							AI Insight
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
							{insight.insight_text}
						</Typography>
					</Box>
				</Stack>
			</Stack>

			<Grid container spacing={3}>
				<Grid item xs={12} md={8}>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6} md={3}>
							<MetricTile
								label="Average Utilization"
								value={
									metrics.average_utilization.value === null
										? "N/A"
										: `${metrics.average_utilization.value.toFixed(2)}%`
								}
								changePct={metrics.average_utilization.change_pct}
								periodWeeks={insight.period_weeks}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<MetricTile
								label="Non-Compliance Issues"
								value={String(metrics.non_compliance_issues.value ?? 0)}
								changePct={metrics.non_compliance_issues.change_pct}
								periodWeeks={insight.period_weeks}
								invertColor
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<MetricTile
								label="Avg Story Points / Hour"
								value={
									metrics.avg_story_points_per_hour.value === null
										? "N/A"
										: metrics.avg_story_points_per_hour.value.toFixed(2)
								}
								changePct={metrics.avg_story_points_per_hour.change_pct}
								periodWeeks={insight.period_weeks}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<MetricTile
								label="Total Bugs Assigned"
								value={String(metrics.total_bugs_assigned.value ?? 0)}
								changePct={metrics.total_bugs_assigned.change_pct}
								periodWeeks={insight.period_weeks}
								invertColor
							/>
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12} md={4}>
					<Divider orientation="vertical" sx={{ display: { xs: "none", md: "block" }, float: "left", mr: 3, height: "100%" }} />
					<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
						Insight Types
					</Typography>
					<Stack spacing={1}>
						{Object.entries(INSIGHT_TYPE_LABELS).map(([key, label]) => (
							<Chip
								key={key}
								size="small"
								label={label}
								color={activeTypes.has(key) ? "primary" : "default"}
								variant={activeTypes.has(key) ? "filled" : "outlined"}
								sx={{ alignSelf: "flex-start" }}
							/>
						))}
					</Stack>
				</Grid>
			</Grid>
		</Paper>
	);
};

export default UtilizationInsightCard;
